<template>
  <div class="d-flex flex-grow-1" style="height: 100%; width: 100%; overflow: hidden;">
    <div :class="['dashboard-filter-sidebar', {'collapsed': !isFilterSidebarShown}]">
      <div :class="['dashboard-filter-inner-wrapper', {'hidden': !isInitialized }]">
        <div class="dashboard-filter me-3">
          <div :class="{'spinner-is-visible': isSpinnerVisible }">
            <fieldset>
              <legend>Filter options</legend>
              <div class="row">
                <div class="col-6"><span class="text-size-small text-color-light">Total assets</span><br><strong>{{ allAssets.length }}</strong></div>
                <div class="col-6 text-end"><span class="text-size-small text-color-light">Filtered assets</span><br><strong>{{ filteredAssets.length }}</strong></div>
              </div>
              <div 
                class="row"
                v-for="item in dataFilter.filterDataFields"
              >
                <div class="col-12">
                  <div class="form-group">
                    <template v-if="item.type=='select' && item.multiple">
                      <label class="placeholder placeholder-inactive">{{ item.label }}</label>
                      <v-select
                        v-model="item.selectedOptions"
                        :multiple="item.multiple"
                        :placeholder="item.placeholder"
                        :ref="item.key + 'Select'"
                        :options="item.options"
                        :label="item.model ? 'name' : ''"
                        @option:selected="applyFilters"
                        @option:deselected="applyFilters"
                      />
                    </template>
                    <template v-if="item.type=='select' && !item.multiple">
                      <label class="placeholder placeholder-inactive">{{ item.label }}</label>
                      <v-select
                        v-model="item.selectedOptions"
                        :multiple="item.multiple"
                        :placeholder="item.placeholder"
                        :ref="item.key + 'Select'"
                        :options="item.options"
                        :label="item.model ? 'name' : ''"
                        @update:modelValue="applyFilters"
                      />
                    </template>
                    <template v-if="item.type=='boolean'">
                      <div class="form-control-toggle">
                        <input 
                          type="checkbox"
                          :id="'chk' + item.key"
                          :ref="item.key + 'Checkbox'"
                          :class="{ 'checked': item.selectedOptions === item.value }"
                          :checked="item.selectedOptions === item.value"
                          @change="item.selectedOptions = $event.target.checked ? item.value : []; applyFilters()">
                        <label :for="'chk' + item.key">
                          <span>{{ item.label }}</span><span></span>
                        </label>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </fieldset>
            <div class="form-group-button">
              <ActionButton
                v-if="!isSpinnerVisible"
                :css="isFilterEmpty ? 'disabled' : ''"
                text="Clear Filter"
                title="Reset Findings Filter"
                icon="autorenew"
                size="md"
                action="default"
                @clickAction="clearFilter"
              />
            </div>
            <div class="spinner"></div>
          </div>
        </div>
      </div>
    </div>
    <div class="d-flex flex-column flex-grow-1" style="overflow: hidden;">
      <div class="d-flex mb-4">
        <div class="col-sm-6">
          <SearchField
            v-model:textSearch="textSearch"
            placeholder="Search by title, pole number and vicinity"
            :delay="1000"
            @search="search"
          />
        </div>
        <div class="col-md-6 col-sm-12 d-flex align-items-center justify-content-end">
          <button
            v-if="isExportEnabled"
            class="btn-expandable btn-expandable-primary btn-expandable-sm me-2"
            title="Export Dashboard Data"
            @click.prevent="exportToCSV"
          >
            <span class="icon" data-icon="file_download"></span><span>Download Data</span>
          </button>
          <button :class="['btn btn-sm btn-info me-2', {'btn-inverse-info': !isFilterSidebarShown}]" @click.prevent="toggleFilterSidebar" style="width: 50px; justify-content: center;" title="Toggle Filter Sidebar">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M16 14H0V2H16V14ZM6 13H15V3H6V13ZM1 8V9H5V8H1ZM1 6V7H5V6H1ZM1 4V5H5V4H1Z"/>
            </svg>
          </button>
          <div class="dashboard-card-toggle btn-group">
            <button :class="['btn btn-sm btn-info', {'btn-inverse-info': screenViewMode != 'map'}]" @click.prevent="toggleScreenViewMode('map')" style="width: 50px; justify-content: center;" title="Map View">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.5 3L5.5 1L0 3V15L5.5 13L10.5 15L16 13V1L10.5 3ZM6 2.27703L10 3.87703V13.7229L6 12.123V2.27703ZM1 3.70044L5 2.24591V12.1178L1 13.5723V3.70044ZM15 12.2996L11 13.7541V3.88225L15 2.42772V12.2996Z"/>
              </svg>
            </button>
            <button :class="['btn btn-sm btn-info', {'btn-inverse-info': screenViewMode != 'split'}]" @click.prevent="toggleScreenViewMode('split')" style="width: 50px; justify-content: center;" title="Split View">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 14H0V2H16V14ZM6 13H10V11H6V13ZM11 13H15V11H11V13ZM1 13H5V11H1V13ZM6 10H10V8H6V10ZM11 10H15V8H11V10ZM1 10H5V8H1V10ZM1 7H15V3H1V7Z"></path>
              </svg>
            </button>
            <button :class="['btn btn-sm btn-info', {'btn-inverse-info': screenViewMode != 'grid'}]" @click.prevent="toggleScreenViewMode('grid')" style="width: 50px; justify-content: center;" title="Grid View">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0 3V14H16V3H0ZM6 10V8H10V10H6ZM10 11V13H6V11H10ZM10 5V7H6V5H10ZM5 5V7H1V5H5ZM1 8H5V10H1V8ZM11 8H15V10H11V8ZM11 7V5H15V7H11ZM1 11H5V13H1V11ZM11 13V11H15V13H11Z"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div
        ref="mainContent"
        class="mainContent d-flex flex-column flex-grow-1" 
        >
        
        <template v-if="screenViewMode === 'map' || screenViewMode === 'split'">
          <div :class="['dashboard-card', {'mb-4': screenViewMode === 'split'}]">
            <AssetsMap 
              ref="assetsMap"
              :assets="filteredAssets"
              :googleMapApiKey="googleMapApiKey"
              :height="mapHeight"
            />
          </div>
        </template>

        <template v-if="screenViewMode === 'grid' || screenViewMode === 'split'">
          <div class="dashboard-card px-3 pb-3 pt-0">
            <AssetsGrid
              ref="assetsGrid"
              :columns="gridColumns"
              :items="filteredAssets"
              :grid-height="gridHeight"
              :grid-width="gridWidth"
              :page-size="100"
              :is-spinner-visible="isSpinnerVisible"
              :is-initialized="isInitialized"
            />
          </div>
        </template>

      </div>
    </div>
  </div>
</template>

<script>
import Moment from "moment";

import Helpers from "../../../helpers.js";

import Screen from "./screen";

export default {
  extends: Screen,

  data: function () {
    return {
      dataFilter: {
        searchDataFields: ["_title", "pole_number", "vicinity"],
        filterDataFields: [
          {
            key: "approved",
            label: "Filtered Approved Records Only",
            options: [],
            selectedOptions: [],
            type: "boolean",
            filter: "exact",
            value: true
          },
          { 
            type: "dataGroups" 
          },
          {
            key: "_status",
            label: "Status",
            placeholder: "Select Status",
            options: [],
            selectedOptions: [],
            type: "select",
            multiple: true,
            filter: "exact",
            model: {
              'Visited': ['Y', 'Y-QC', 'Y-H20', 'Y-H20-QC'],
              'Rejected': ['Y-R', 'Y-R-QC', 'Y-NRR', 'Y-NRR-QC', 'Y-RR', 'Y-RR-QC', 'Y-UGR', 'Y-UGR-QC'],
              'Priority': ['Y-PRI', 'Y-PRI-QC'],
              'Not Visited': ['N', 'N-Pad', 'NI'],
            },
          },
          {
            key: "manufacturer",
            label: "Manufacturer",
            placeholder: "Select Manufacturer",
            options: [],
            selectedOptions: [],
            type: "select",
            multiple: true,
            filter: "exact",
          },
          {
            key: "inspection_type",
            label: "Inspection Type",
            placeholder: "Select Inspection Type",
            options: [],
            selectedOptions: [],
            type: "select",
            multiple: false,
            filter: "exact",
          },
          {
            key: "maintenance_issues",
            label: "Maintenance Issue",
            placeholder: "Select Issue",
            options: [],
            selectedOptions: [],
            type: "select",
            multiple: false,
            filter: "contains",
            method:  "getMaintenanceIssues"
          }
        ]
      },

      gridColumns: [
        {
          field: "pole_number",
          label: "Pole Number",
          type: "string",
          width: 150,
          align: "left",
          sortable: true,
          fixed: true,
          showDetails: true
        },
        {
          field: "_title",
          label: "Asset Title",
          type: "string",
          width: 150,
          align: "left",
          sortable: true,
        },
        {
          field: "inspection_type",
          label: "Inspection Type",
          type: "string",
          width: 180,
          align: "left",
          sortable: false
        },
        {
          field: "maintenance_issues",
          label: "Maintenance Issues",
          type: "string",
          width: 180,
          align: "left",
          sortable: false,
          method: "formatMaintenanceIssues"
        },
        {
          field: "species",
          label: "Species",
          type: "string",
          width: 100,
          align: "left  ",
          sortable: true
        },
        {
          field: "class",
          label: "Class",
          type: "string",
          width: 80,
          align: "center",
          sortable: true
        },
        {
          field: "length",
          label: "Length (ft)",
          type: "number",
          width: 100,
          align: "center",
          sortable: true
        },
        {
          field: "damagedecay_present",
          label: "Damage/Decay",
          type: "center",
          width: 150,
          align: "center",
          sortable: true
        },
        {
          field: "manufacturer",
          label: "Manufacturer",
          type: "string",
          width: 150,
          align: "left",
          sortable: false
        },
        {
          field: "year",
          label: "Year",
          type: "string",
          width: 100,
          align: "right",
          sortable: true
        },
        {
          computed: "hasPhotos",
          label: "Photos",
          type: "string",
          width: 100,
          align: "center",
          sortable: false
        },
      ]
    }
  },

  setup () {
    return {
      h$: Helpers,
      m$: Moment
    }
  }
}
</script>
